<template>
  <div class="row g-4">
    <!-- Carte : URL de notification (webhook) -->
    <div class="col-lg-6">
      <div class="card mb-0 border-0 rounded-2 bg-white letter-spacing shadow-sm h-100">
        <div class="card-body p-20 p-md-25 p-lg-30">
          <div class="mb-15 mb-lg-20">
            <h5 class="card-title fw-bold mb-2">{{ $t('generalConfig.notificationUrlConfig') }}</h5>
            <p class="text-muted mb-0 fs-13">{{ $t('generalConfig.notificationUrlConfigHint') || 'Recevez vos notifications de transactions sur cette URL.' }}</p>
          </div>

          <div class="config-section-box p-15 p-lg-20">
            <form id="callback" @submit.prevent="storeUrl" class="row g-3 needs-validation" novalidate>
              <div v-if="errors!==''" class="col-12">
                <div class="alert alert-danger py-10 px-15 mb-0 fs-14">
                  <div v-if="errorId" class="fw-semibold">{{ errorId }}</div>
                  <div>{{ errors }}</div>
                </div>
              </div>

              <div class="col-12">
                <label for="validationCustom01" class="form-label fw-medium">{{ $t('generalConfig.url') }}</label>
                <input
                  type="text"
                  class="form-control shadow-none fs-md-15 text-black"
                  id="validationCustom01"
                  :placeholder="$t('generalConfig.urlPlaceholder')"
                  required
                  v-model="formValues.url"
                  :class="{ 'is-invalid': errorsBis['url'] }"
                  @blur="validate('url')"
                />
                <span class="invalid-feedback">{{ errorsBis['url']}}</span>
              </div>

              <div class="col-12 d-flex justify-content-end flex-wrap gap-10 mt-10">
                <button
                  :disabled="!btnLoad1"
                  class="btn btn-outline-secondary d-inline-flex align-items-center gap-8"
                  type="button"
                  @click="generateSecretKey"
                  v-if="current_permissions('config_webhook_token')"
                >
                  <span v-if="!btnLoad1" class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>
                  {{ $t('generalConfig.generateSecretKey') }}
                </button>
                <button
                  :disabled="!btnLoad"
                  class="btn text-white d-inline-flex align-items-center gap-8"
                  type="submit"
                  style="background-color: #EB9355;"
                  v-if="current_permissions('config_webhook_url')"
                >
                  <span v-if="!btnLoad" class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true" style="color: #FFF;"></span>
                  {{ $t('generalConfig.saveUrl') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Carte : Logo de l'organisation -->
    <div class="col-lg-6">
      <div class="card mb-0 border-0 rounded-2 bg-white letter-spacing shadow-sm h-100">
        <div class="card-body p-20 p-md-25 p-lg-30">
          <div class="mb-15 mb-lg-20">
            <h5 class="card-title fw-bold mb-2">{{ $t('generalConfig.logoConfig') }}</h5>
            <p class="text-muted mb-0 fs-13">{{ $t('generalConfig.logoConfigHint') || 'Ce logo est utilisé sur vos documents et votre espace.' }}</p>
          </div>

          <div class="config-section-box p-15 p-lg-20">
            <form id="callback1" @submit.prevent="updateImage" class="row g-3 align-items-center">
              <div v-if="errorsLogo!==''" class="col-12">
                <div class="alert alert-danger py-10 px-15 mb-0 fs-14">
                  <div v-if="errorIdLogo" class="fw-semibold">{{ errorIdLogo }}</div>
                  <div>{{ errorsLogo }}</div>
                </div>
              </div>

              <div class="col-auto">
                <div class="logo-preview-wrapper">
                  <img v-if="image" :src="image" class="logo-preview-img" />
                  <img v-else-if="imgSrc" :src="imgSrc" class="logo-preview-img" />
                  <div v-else class="logo-preview-placeholder">
                    {{ (organization?.name || '?').charAt(0).toUpperCase() }}
                  </div>
                </div>
              </div>

              <div class="col">
                <label for="logo" class="form-label fw-medium">{{ $t('generalConfig.logo') }}</label>
                <input
                  type="file"
                  class="form-control shadow-none fs-md-15 text-black"
                  id="logo"
                  accept="image/*"
                  @change="handleImageUpload"
                  required
                />
                <span class="fs-12 text-muted d-block mt-6">
                  {{ $t('generalConfig.logoSizeHint', { size: maxImgSizeKb }) || `Taille maximum : ${maxImgSizeKb} ko` }}
                </span>
              </div>

              <div class="col-12 d-flex justify-content-end mt-10">
                <button
                  :disabled="!btnLoadLogo"
                  class="btn text-white d-inline-flex align-items-center gap-8"
                  type="submit"
                  style="background-color: #EB9355;"
                  v-if="current_permissions('org_logo')"
                >
                  <span v-if="!btnLoadLogo" class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true" style="color: #FFF;"></span>
                  {{ $t('generalConfig.saveLogo') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Carte : Validation des transactions -->
    <div class="col-lg-6">
      <div class="card mb-0 border-0 rounded-2 bg-white letter-spacing shadow-sm h-100">
        <div class="card-body p-20 p-md-25 p-lg-30">
          <div class="mb-15 mb-lg-20">
            <h5 class="card-title fw-bold mb-2">{{ $t('generalConfig.transactionValidation') }}</h5>
            <p class="text-muted mb-0 fs-13">{{ $t('generalConfig.transactionValidationHint') || 'Exigez une validation manuelle avant traitement des paiements sortants.' }}</p>
          </div>

          <div class="config-section-box p-15 p-lg-20">
            <form id="callback2" @submit.prevent="updateTransactionValidationConfig" class="row g-3">
              <div v-if="errorsValidation!==''" class="col-12">
                <div class="alert alert-danger py-10 px-15 mb-0 fs-14">
                  <div v-if="errorIdValidation" class="fw-semibold">{{ errorIdValidation }}</div>
                  <div>{{ errorsValidation }}</div>
                </div>
              </div>

              <div class="col-md-6">
                <label for="status" class="form-label fw-medium">{{ $t('generalConfig.validateTransactions') }}</label>
                <select
                  name="status"
                  id="status"
                  class="form-select fs-md-15 text-black shadow-none"
                  @change="handleValidationChange"
                  required
                >
                  <option value="INACTIVE" :selected="selectedValidation==false">{{ $t('generalConfig.inactive') }}</option>
                  <option value="ACTIVE" :selected="selectedValidation==true">{{ $t('generalConfig.active') }}</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="d-block text-black fw-medium mb-10" for="duration">{{ $t('generalConfig.linkValidityDuration') }}</label>
                <input
                  type="number"
                  min="0"
                  class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16"
                  :placeholder="$t('generalConfig.durationPlaceholder')"
                  v-model="duration"
                  id="duration"
                  @keyup="validate_phone_number($event.target.value,$event.target,3)"
                />
              </div>

              <div class="col-12 d-flex justify-content-end mt-10">
                <button
                  :disabled="!btnLoadValidation"
                  class="btn text-white d-inline-flex align-items-center gap-8"
                  type="submit"
                  style="background-color: #EB9355;"
                  v-if="current_permissions('edit_organization')"
                >
                  <span v-if="!btnLoadValidation" class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true" style="color: #FFF;"></span>
                  {{ $t('generalConfig.save') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Secret Key -->
  <div class="modal fade" id="filterLeadModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h1 class="modal-title fs-5">{{ $t('generalConfig.secretKey') }}</h1>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <label class="d-block text-black fw-semibold mb-10">{{ $t('generalConfig.yourSecretKey') }}</label>
          <div class="row form-control shadow-none d-flex flex-row align-items-center mx-0">
            <input
              type="text"
              readonly
              class="col fw-medium text-black fs-md-15 fs-lg-16 border-0"
              id="secretKeyInput"
              v-model="secretKey"
              style="background: transparent; font-family: 'Courier New', monospace;"
            />
            <div class="col-auto ps-0">
              <a href="#" class="text-decoration-none d-block text-center fw-semibold" style="color: #EB9355;" @click.prevent="copyToCliboard">
                {{ $t('generalConfig.copy') || 'Copier' }}
              </a>
            </div>
          </div>

          <div class="alert mt-15 mb-0 py-10 px-15 fs-14" style="background-color: #FFF6E9; border: 1px solid #F3D9AE; color: #7A5211;">
            {{ $t('generalConfig.secretKeyNote') }}
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn text-white" style="background-color: #EB9355;" @click.prevent="copyToCliboard">
            {{ $t('generalConfig.copy') || 'Copier' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <div id="snackbar"></div>
</template>

<script setup  lang="ts">

import { ref, onMounted, onBeforeMount, reactive, inject, computed } from "vue";
import * as yup from 'yup';
import axios from 'axios';
import { Modal } from "bootstrap";
import { useI18n } from "vue-i18n";
import { current_permissions } from '../../utils/permission';
import { redirect } from '../../utils/services';
import router from '../../router/index'
import { showToast, checkSessionValidate, img_src, validate_phone_number } from "@/utils/function";
import { Business, Logo, Organization } from "@/utils/Model";

const filename = ref('');
const image = ref('');
const selectedValidation = ref(false);
const organization = ref({} as Organization);
const logo = ref(new Array<Logo>());
const secretKey = ref('');
const duration = ref(0);
const backApi = inject('back');
const btnLoad = ref(true);
const { t } = useI18n();
const btnLoad1 = ref(true);
const btnLoadLogo = ref(true);
const errorsLogo = ref('');
const errorIdLogo = ref('');
const errors = ref('');
const errorId = ref('');
const success = ref('');
const btnLoadValidation = ref(true);
const errorsValidation = ref('');
const errorIdValidation = ref('');
const errorsBis = { 'url': '' };
const formValues = reactive({
  url: ""
});

// AJOUTÉ: taille max affichée dans le hint sous le champ logo, dérivée de la même
// variable d'environnement utilisée pour la validation (au lieu de la répéter à la main)
const maxImgSizeKb = process.env.VUE_APP_IMG_SIZE;

const schema = yup.object({
  url: yup.string().required('L\'url de notification est requise'),
});
const token = localStorage.getItem('login_token');
let codeOrganization: string | null = localStorage.getItem('organization_code');

function handleValidationChange(event) {
  selectedValidation.value = false;
  if (event.target.value == 'ACTIVE') {
    selectedValidation.value = true;
  }
}

onMounted(() => {
  checkSessionValidate(1);
  getCallBackUrl();
  getLogo();
  showOrganizationInfo();
});

const storeUrl = () => {
  btnLoad.value = false;
  try {
    schema.validateSync(formValues);
    axios.post(`${backApi}/secure/webhooks/url`, {
      url: formValues.url,
      organization: codeOrganization,
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    }).then((response) => {
      btnLoad.value = true;
      if (response.status === 200) {
        showToast("Enregistrement effectué avec succès", "green");
      }
    }).catch((error) => {
      btnLoad.value = true;
      if (error.response) {
        if (error.response.status === 401) {
          router.push('/')
        } else {
          errorId.value = error.response.data.errorId;
          errors.value = error.response.data.message;
        }
      }
    });
  } catch (error) {
    btnLoad.value = true;
    errors.value = "une erreur inattendue est apparue"
  }
}

// MODIFIÉ: ouverture du modal via l'API Bootstrap (Modal.getOrCreateInstance) au lieu
// du bouton caché déclenché par un faux clic — plus robuste et plus lisible.
const generateSecretKey = () => {
  btnLoad1.value = false;
  try {
    axios.post(`${backApi}/secure/webhooks/token`, {
      url: formValues.url,
      organization: codeOrganization,
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    }).then((response) => {
      btnLoad1.value = true;
      if (response.status === 200) {
        secretKey.value = response.data.webhookToken;
        const modalEl = document.getElementById('filterLeadModal');
        const modalInstance = Modal.getOrCreateInstance(modalEl);
        modalInstance.show();
      }
    }).catch((error) => {
      btnLoad1.value = true;
      if (error.response) {
        if (error.response.status === 401) {
          router.push('/')
        } else {
          errorId.value = error.response.data.errorId;
          errors.value = error.response.data.message;
        }
      }
    });
  } catch (error) {
    btnLoad1.value = true;
    errors.value = "une erreur inattendue est apparue"
  }
}

const getCallBackUrl = () => {
  axios.get(backApi + '/secure/webhooks/show-webhook', {
    headers: {
      Authorization: `Bearer ${token}`
    }
  })
    .then((response) => {
      formValues.url = response.data
    }).catch((error) => {
      if (error.response) {
        if (error.response.status === 401) {
          router.push('/')
        }
      }
    });
}

function validate(field) {
  schema
    .validateAt(field, formValues)
    .then(() => {
      errorsBis[field] = "";
    })
    .catch(err => {
      errorsBis[field] = err.message;
    });
  errors.value = '';
  success.value = '';
}

function copyToCliboard() {
  navigator.clipboard.writeText(secretKey.value);
  showToast("Copié", "green");
}

const handleImageUpload = (event) => {
  const file = event.target.files[0]
  const fileSize = file.size / 1024;
  if (fileSize <= parseInt(process.env.VUE_APP_IMG_SIZE)) {
    filename.value = file;
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      image.value = reader.result
    }
  } else {
    showToast("la taille maximum autorisée est " + process.env.VUE_APP_IMG_SIZE + " kb!", "red");
    (document.getElementById("logo") as HTMLInputElement).value = '';
  }
}

const updateImage = () => {
  btnLoadLogo.value = false;
  const formdata = new FormData();
  formdata.append('file', filename.value);
  formdata.append('organization', codeOrganization);
  axios.post(backApi + '/secure/organizations/logo', formdata, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  })
    .then((response) => {
      btnLoadLogo.value = true;
      if (response.status === 200) {
        logo.value = response.data;
        showToast("Enregistrement effectué avec succès", "green");
      }
    })
    .catch((error) => {
      btnLoadLogo.value = true;
      if (error.response) {
        if (error.response.status === 401) {
          router.push('/')
        } else {
          errorIdLogo.value = error.response.data.errorId;
          errorsLogo.value = error.response.data.message;
        }
      }
    })
};

const getLogo = () => {
  axios.get(backApi + `/secure/organizations/${codeOrganization}/logo`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  })
    .then((response) => {
      logo.value = response.data;
    }).catch((error) => {
      if (error.response) {
        if (error.response.status === 401) {
          router.push('/')
        }
      }
    });
}

const imgSrc = computed(() => {
  const temp = logo.value;
  return img_src(temp[0]?.storageUrl)
})

const showOrganizationInfo = () => {
  axios.get(backApi + '/secure/organizations/' + codeOrganization + '/show', {
    headers: {
      Authorization: `Bearer ${token}`
    }
  })
    .then((response) => {
      organization.value = response.data;
      selectedValidation.value = response.data.validatePayoutBeforProcess;
      duration.value = response.data.payoutValidationDelay;
    }).catch((error) => {
      if (error.response) {
        if (error.response.status === 401) {
          router.push('/')
        }
      }
    });
}

const updateTransactionValidationConfig = () => {
  btnLoadValidation.value = false;

  axios.put(backApi + '/secure/organizations/' + codeOrganization + '/edit',
    {
      validatePayoutBeforProcess: selectedValidation.value,
      payoutValidationDelay: duration.value,
    }, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  })
    .then((response) => {
      btnLoadValidation.value = true;
      if (response.status === 200) {
        showToast("Enregistrement effectué avec succès", "green");
      }
    })
    .catch((error) => {
      btnLoadValidation.value = true;
      if (error.response) {
        if (error.response.status === 401) {
          router.push('/')
        } else {
          errorIdValidation.value = error.response.data.errorId;
          errorsValidation.value = error.response.data.message;
        }
      }
    })
};
</script>

<style scoped>
.config-section-box {
  background-color: #FAFAFA;
  border: 1px solid #EFEFEF;
  border-radius: 8px;
}

.logo-preview-wrapper {
  width: 72px;
  height: 72px;
}

.logo-preview-img {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #EFEFEF;
}

.logo-preview-placeholder {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background-color: #FFF1E7;
  color: #EB9355;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  border: 2px dashed #F3D9AE;
}

@media (max-width: 575px) {
  .logo-preview-wrapper,
  .logo-preview-img,
  .logo-preview-placeholder {
    width: 56px;
    height: 56px;
  }
}
</style>